import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { INITIAL_STATE } from "./consts";
import { proxyClient } from "~/utils/api";
import { SearchResultGroup } from "../types";

// // // //

/**
 * A Redux thunk that fetches the initial search results from the server via tRPC.
 * @returns {Promise<SearchResultGroup[]>} A Promise that resolves with the initial SearchResultGroup data.
 */
export const fetchInitialSearchGroups = createAsyncThunk(
    "search/getInitialSearchGroups",
    async () => {
        // NOTE - we add the `skipBatch` context to the request so this query is NOT batched with other requests
        // This improves response time for the initial search state load
        // Doc: https://trpc.io/docs/v9/links#disable-batching-for-certain-requests
        const response = await proxyClient.search.getInitialSearchGroups.query(
            undefined,
            {
                context: {
                    skipBatch: true,
                },
            },
        );
        return response;
    },
);

/**
 * The search slice of the Redux store, created using the createSlice function from @reduxjs/toolkit.
 * @typedef {Object} SearchSlice
 * @property {Object} initialState - The initial state of the search slice.
 * @property {Function} reducers - The reducer functions for the search slice.
 * @property {Function} extraReducers - The extra reducer functions for the search slice.
 */
export const searchSlice = createSlice({
    name: "search",
    initialState: INITIAL_STATE,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchInitialSearchGroups.pending, (state) => {
                state.loading = true;
                state.initialFetchComplete = false;
                state.error = null;
            })
            .addCase(fetchInitialSearchGroups.fulfilled, (state, action) => {
                state.loading = false;
                state.initialFetchComplete = true;
                state.groups = action.payload as unknown as SearchResultGroup[]; // ENHANCEMENT - cleanup this type coercion
            })
            .addCase(fetchInitialSearchGroups.rejected, (state, action) => {
                state.loading = false;
                state.initialFetchComplete = false;
                state.error =
                    action.error.message ??
                    "An error occurred while fetching initial search state";
            });
    },
});

/**
 * The reducer function for the search slice of the Redux store.
 */
export const reducer = searchSlice.reducer;
